import { useNavigate } from 'react-router-dom'
import { FlaskConical, KeyRound, Puzzle, Database, MoreHorizontal } from 'lucide-react'
import TarjetaMigracion from '@/components/cd2b/TarjetaMigracion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { diasParaVencer } from './paquete'

/** Enlace en línea a otro paso del asistente (sustituye a los «más abajo» / «arriba»). */
export function IrAPaso({ n, onIr, children }) {
  return (
    <button type="button" className="font-medium text-primary underline underline-offset-2" onClick={() => onIr(n)}>
      {children}
    </button>
  )
}

/**
 * La empresa demo del dueño: crearla, recuperar su acceso, extender la prueba, migración y
 * plugins a medida. Solo existe con la cotización ya guardada.
 */
export default function TarjetaDemo({ c, onIrAPaso }) {
  const navigate = useNavigate()
  const { id, form, propuesta, demoInfo, esNube, tieneDemo, puedeCrearDemo, paisFrena, paisSinGuardar, catalogoFrena, catalogoSinDeclarar, appMovilVendida } = c
  const solicitud = propuesta?.solicitudPrueba?.at && !propuesta.solicitudPrueba.atendidaAt ? propuesta.solicitudPrueba : null

  return (
    <Card id="demo" className="scroll-mt-24">
      <CardHeader>
        <CardTitle className="text-h3">Empresa demo del dueño</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
          Crea una empresa de prueba para el dueño y comparte el código con él para que la pruebe
          desde la app (escritorio/móvil). Cuando la apruebe y pague, se promueve a producción sin perder datos.
        </p>

        {/* «Probar primero»: el dueño la pidió desde su cotización. Al crearla (o extenderla) le llega su acceso. */}
        {solicitud && (
          <Alert variant="info">
            <AlertDescription>
              <strong>El dueño {solicitud.tipo === 'extension' ? 'pidió más días de prueba' : 'quiere probar antes de comprar'}</strong>
              {' '}({new Date(solicitud.at).toLocaleDateString()}).{' '}
              {solicitud.tipo === 'extension'
                ? 'Extiende la prueba y le avisamos por correo.'
                : 'Crea la empresa demo: al terminar, le llega su acceso por correo.'}
            </AlertDescription>
          </Alert>
        )}

        {!esNube && !tieneDemo && (
          <Alert variant="warning">
            <AlertDescription>
              Para crear la empresa demo, cambia la <strong>Modalidad</strong> a “Nube gestionada”
              (<IrAPaso n={3} onIr={onIrAPaso}>paso 3, Arquitectura de datos</IrAPaso>) y guarda.
            </AlertDescription>
          </Alert>
        )}

        {/*
          Un botón deshabilitado sin motivo es una trampa: el asesor lo pulsa, no pasa nada, y
          no tiene forma de saber qué le falta. El detalle está abajo, en el asistente del
          catálogo; aquí basta con decir POR QUÉ y adónde ir.
        */}
        {paisFrena && !tieneDemo && (
          <Alert variant="warning">
            <AlertDescription>
              {paisSinGuardar
                ? <>Elegiste el país <strong>{form.pais}</strong> pero falta <strong>guardar</strong> la cotización.</>
                : <>Falta el <strong>país</strong> de la empresa (<IrAPaso n={1} onIr={onIrAPaso}>paso 1, Cliente y país</IrAPaso>).
                  De él dependen el catálogo contable y la facturación, así que no se crea sin país. Elígelo y guarda.</>}
            </AlertDescription>
          </Alert>
        )}

        {catalogoFrena && !tieneDemo && (
          <Alert variant="warning">
            <AlertDescription>
              {catalogoSinDeclarar ? (
                <>
                  Falta decir si este cliente <strong>trae el catálogo de cuentas de su contador</strong>.
                  De eso depende con qué plan de cuentas nace la empresa, así que no se crea sin esa
                  respuesta. Son dos botones, en «Catálogo de cuentas», más abajo.
                </>
              ) : (
                <>
                  Este cliente trae el <strong>catálogo de cuentas de su contador</strong>, y todavía no
                  está confirmado. La empresa no se crea hasta entonces: nacería contabilizando contra un
                  plan que su contador no autorizó. Completá el asistente de más abajo.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/*
          Los cuatro botones NO son del mismo rango, y ponerlos iguales obligaba a
          leerlos todos cada vez para encontrar el que tocaba:
            · crear la demo es de CICLO DE VIDA y solo se hace una vez → primario, y
              desaparece en cuanto existe;
            · ver el acceso es una CONSULTA que se repite → secundario, siempre a mano;
            · pedir un plugin es un TRÁMITE ajeno a esta pantalla → al menú de más;
            · la migración es un PROCESO CON ESTADO que dura días → tarjeta propia.
        */}
        <div className="flex flex-wrap items-center gap-2">
          {!tieneDemo && (
            <Button disabled={!puedeCrearDemo} onClick={c.crearDemo}>
              <FlaskConical size={16} className="mr-1.5" /> Crear empresa demo
            </Button>
          )}
          {tieneDemo && (
            <Button variant="outline" onClick={c.verAcceso}>
              <KeyRound size={16} className="mr-1.5" /> Ver acceso del dueño
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="Más acciones">
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={c.solicitarPlugin}>
                <Puzzle size={16} className="mr-2" /> Solicitar plugin a medida
              </DropdownMenuItem>
              {tieneDemo && (
                <DropdownMenuItem onClick={() => navigate(`/consultor/cotizaciones/${id}/migracion`)}>
                  <Database size={16} className="mr-2" /> Llevarse los datos (exportar)
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {tieneDemo && <TarjetaMigracion propuestaId={id} enProduccion={propuesta?.estado === 'activada'} />}

        {tieneDemo && !demoInfo && (
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            Empresa demo creada. Usa “Ver acceso del dueño” para recuperar el código y el PIN.
          </p>
        )}

        {/* Una demo es un tenant nube real, con su cuota de DTE: tiene fecha de caducidad. */}
        {tieneDemo && propuesta?.demo?.expiraAt && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-caption ${diasParaVencer(propuesta.demo.expiraAt) < 0 ? 'text-danger-700 dark:text-danger-300' : 'text-neutral-500 dark:text-neutral-400'}`}>
              {diasParaVencer(propuesta.demo.expiraAt) < 0
                ? 'La prueba venció: el dueño ya no puede ver sus credenciales.'
                : `La prueba vence en ${diasParaVencer(propuesta.demo.expiraAt)} día(s).`}
            </span>
            <Button variant="ghost" size="sm" onClick={c.extenderDemo}>Extender 15 días</Button>
          </div>
        )}

        {demoInfo?.negocioCodigo && (
          <Alert>
            <AlertDescription>
              <div className="space-y-3">
                <p className="font-medium">Credenciales para el dueño:</p>

                <div className="space-y-1">
                  <p className="font-medium text-neutral-900 dark:text-neutral-50">App de escritorio (pestaña “Cuenta”)</p>
                  {demoInfo.cuentaExistente ? (
                    <p className="text-caption text-neutral-600 dark:text-neutral-400">
                      Este correo ya tiene una cuenta en uso: el dueño entra con <strong>su propia contraseña</strong>
                      {' '}(o “¿Olvidaste tu contraseña?”). No se modificó.
                    </p>
                  ) : (
                    <>
                      <p className="break-all">Correo:{' '}
                        <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">{demoInfo.correo}</code>
                      </p>
                      {demoInfo.passwordTemp && (
                        <p className="break-all">Contraseña temporal:{' '}
                          <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">{demoInfo.passwordTemp}</code>
                        </p>
                      )}
                      <p className="text-caption text-neutral-500 dark:text-neutral-400">
                        {demoInfo.passwordCambiadaPorDueno
                          ? 'El dueño ya cambió su contraseña: esta temporal probablemente ya no sirve.'
                          : 'Que la cambie al entrar (Perfil → contraseña).'}
                      </p>
                    </>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="font-medium text-neutral-900 dark:text-neutral-50">App móvil (código de empresa)</p>
                  {appMovilVendida ? (
                    <>
                      <p>Código de empresa:{' '}
                        <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">{demoInfo.negocioCodigo}</code>
                      </p>
                      {demoInfo.usuarioAdmin && (
                        <p>Usuario:{' '}
                          <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">{demoInfo.usuarioAdmin}</code>
                          {' '}<span className="text-caption text-neutral-500 dark:text-neutral-400">(o “admin”)</span>
                        </p>
                      )}
                      {demoInfo.pinAdmin && (
                        <p>PIN:{' '}
                          <code className="font-mono font-semibold text-neutral-900 dark:text-neutral-50">{demoInfo.pinAdmin}</code>
                        </p>
                      )}
                    </>
                  ) : (
                    /*
                      Sin el canal vendido la app rechaza el código en la puerta. Entregarlo igual
                      mandaba al dueño contra un 402 que dice "pídele a tu asesor que la agregue"
                      —cuando el asesor está justo aquí, sin saber que le falta marcarla.
                    */
                    <p className="text-caption text-neutral-600 dark:text-neutral-400">
                      Este paquete <strong>no incluye la app del teléfono</strong>: el dueño probará
                      desde la computadora. Para que la pruebe en su teléfono, márcala en
                      el <IrAPaso n={2} onIr={onIrAPaso}>paso 2, Paquete</IrAPaso> y <strong>guarda</strong>;
                      el código de empresa aparecerá aquí.
                    </p>
                  )}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
